/**
 * What the server tells a storefront page before any script runs (siteSeo.js
 * writes `window.__SITE_BOOT__` and starts the page's public API requests,
 * `window.__SITE_PREFETCH__`, so they download in parallel with the bundle).
 */
export interface SiteBoot {
  orgSlug: string;
  /** The org's nav logo for the loading spinner (shared/pageBlocks navSplashLogo); absent = the Sleepy logo. */
  splashLogo?: string;
}

declare global {
  interface Window {
    __SITE_BOOT__?: SiteBoot;
    /** Public API requests the server's inline script already started, by path. */
    __SITE_PREFETCH__?: Record<string, Promise<Response | null>>;
  }
}

export function readSiteBoot(): SiteBoot | null {
  const boot = typeof window !== 'undefined' ? window.__SITE_BOOT__ : undefined;
  return boot && typeof boot.orgSlug === 'string' && boot.orgSlug ? boot : null;
}

// The logo the loading spinner shows while a site is on screen: seeded from the server's boot data, then kept current
// by SitePage from the nav it loads, and cleared when the visitor leaves the site for the app.
let brandLogo: string | null = readSiteBoot()?.splashLogo || null;

/** The current site's spinner logo, or null for the default Sleepy logo. */
export function siteBrandLogo(): string | null {
  return brandLogo;
}

export function setSiteBrandLogo(url: string | null): void {
  brandLogo = url || null;
}

/**
 * Takes the response of a request the page already started for `path`, once.
 * Null when there is none (or it failed): the caller then makes its own request.
 */
export async function takePrefetched(path: string): Promise<Response | null> {
  const started = typeof window !== 'undefined' ? window.__SITE_PREFETCH__?.[path] : undefined;
  if (!started) return null;
  delete window.__SITE_PREFETCH__![path];
  try {
    const response = await started;
    return response && response.ok ? response : null;
  } catch {
    return null;
  }
}
